import { useNavigate, useParams } from "react-router";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { useGetDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import type { ApiDealShortlistResponse } from "@vega-api/generated/types/apiDealShortlistResponse";
import { EmptyCard } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { Skeleton } from "@vega-core/components/shadcn/skeleton";
import { m } from "@vega-core/paraglide/messages";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useFeatureFlagGuard } from "@vega-modules/hooks/useFeatureFlagGuard";
import { CoInvestmentFullPaths, CommonPaths } from "@vega-modules/paths";

import { CoInvestmentPageHeader } from "../components/CoInvestmentPageHeader/CoInvestmentPageHeader";
import { LpNdaTable } from "../components/LpNdaTable/LpNdaTable";
import { LpNdaUploadSection } from "../components/LpNdaUploadSection/LpNdaUploadSection";
import { NdaUploadSection } from "../components/NdaUploadSection/NdaUploadSection";
import { useDealName } from "../hooks/useDealName";
import { useNdaUpload } from "../hooks/useNdaUpload";

export const NdaManagementPage = () => {
  const { id: dealIdParams } = useParams<{ id: string }>();
  const dealId = dealIdParams ?? "";
  const navigate = useNavigate();

  const { featureFlag } = useFeatureFlag();
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];

  useFeatureFlagGuard(
    "gp_co_investment",
    isGPCoInvestPortal ? undefined : CommonPaths.Home,
  );

  const { dealName, isLoading: isDealNameLoading } = useDealName(dealId);

  const {
    dealGPViewResponse,
    isPendingGPView,
    uploadPdfMutation,
    uploadWordMutation,
    pdfInputRef,
    wordInputRef,
    handlePdfFileSelect,
    handleWordFileSelect,
  } = useNdaUpload(dealId);

  const { data: hasLps, isLoading: isShortlistLoading } = useGetDealShortlist(
    dealId,
    undefined,
    {
      query: {
        enabled: isTruthy(dealId),
        select: ({ data }: ApiDealShortlistResponse) =>
          (data?.dealShortlistItems.length ?? 0) > 0,
      },
    },
  );

  return (
    <div
      className="my-4 max-w-3xl mx-auto"
      data-testid="co-investments-nda-management"
    >
      <CoInvestmentPageHeader
        backButtonDataTestId="back-to-configure-button"
        capitalize={false}
        fundName={dealName}
        isLoading={isPendingGPView || isDealNameLoading}
        onBack={
          isNewNavEnabled
            ? () =>
                navigate(
                  CoInvestmentFullPaths.CoInvestmentConfigure.replace(
                    ":id",
                    dealId,
                  ),
                )
            : undefined
        }
        showClosedDealNotice
        showDrawer={!isNewNavEnabled}
        title={m.coInvestments_nav_ndaManagement()}
      />

      <div className="space-y-12 mt-8">
        <div>
          <h2 className="heading-5 text-text/primary">
            {m.coInvestments_ndaManagement_globalNda_title()}
          </h2>
          <p className="body-base-regular text-text/secondary mb-4">
            {m.coInvestments_ndaManagement_globalNda_description()}
          </p>
          <NdaUploadSection
            heading={null}
            isPendingPdf={uploadPdfMutation.isPending}
            isPendingWord={uploadWordMutation.isPending}
            nda={dealGPViewResponse?.data?.nda}
            onPdfFileSelect={handlePdfFileSelect}
            onWordFileSelect={handleWordFileSelect}
            pdfInputRef={pdfInputRef}
            wordInputRef={wordInputRef}
          />
        </div>

        <div>
          <h2 className="heading-5 text-text/primary">
            {m.coInvestments_lpSpecificNdas()}
          </h2>
          <p className="body-base-regular text-text/secondary mb-4">
            {m.coInvestments_ndaManagement_lpSpecificNdas_description()}
          </p>
          {isShortlistLoading ? (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : null}
          {!isShortlistLoading && !hasLps ? (
            <EmptyCard
              description={m.coInvestments_manageLpSpecificNdas_noLps()}
              title={m.coInvestments_manageLpSpecificNdas_noLps_title()}
            />
          ) : null}
          {!isShortlistLoading && hasLps ? (
            <>
              <LpNdaUploadSection dealId={dealId} />
              <LpNdaTable dealId={dealId} />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
